"use client";

import * as SelectPrimitive from "@radix-ui/react-select";
import * as React from "react";

import {
  getFlatOptions,
  isGroupedOptions,
  OptionsSorting,
  sortOptions,
} from "../lib/optionUtils";
import { cn } from "../lib/utils";
import { Options } from "../types.ts/types";
import { buttonVariants } from "./button";
import { Icon } from "./icon";
import { inputSectionClasses } from "./input";

interface SelectProps extends React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Root
> {
  allowDeselect?: boolean;
  "aria-describedby"?: string;
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
  className?: string;
  /**
   * Forwarded to the trigger so a `<label htmlFor>` (e.g. FormLabel via
   * FormControl's Slot) can target it — SelectPrimitive.Root renders no DOM
   * node, so props left on it are silently dropped.
   */
  id?: string;
  onValueChange: React.ComponentPropsWithRef<
    typeof SelectPrimitive.Root
  >["onValueChange"];
  options: Options;
  placeholder?: string;
  selectContentProps?: React.ComponentPropsWithoutRef<
    typeof SelectPrimitive.Content
  >;
  showCheck?: boolean;
  sorting?: OptionsSorting;
  trigger?: React.ReactNode;
  value: React.ComponentPropsWithRef<typeof SelectPrimitive.Root>["value"];
}

interface DefaultSelectTriggerProps extends React.ComponentPropsWithoutRef<
  typeof SelectPrimitive.Trigger
> {
  allowDeselect?: SelectProps["allowDeselect"];
  onValueChange: SelectProps["onValueChange"];
  placeholder?: SelectProps["placeholder"];
  value: SelectProps["value"];
}

// eslint-disable-next-line react/display-name -- lint-backlog: anonymous forwardRef/memo component; add a displayName in a follow-up
const DefaultSelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  DefaultSelectTriggerProps
>(
  (
    {
      allowDeselect = true,
      className,
      placeholder = "Select",
      value,
      onValueChange,
      ...props
    },
    ref,
  ) => (
    <SelectTrigger
      className={cn(
        buttonVariants({ variant: "outline" }),
        "relative justify-start gap-0 bg-card pl-3 pr-8 font-normal",
        className,
      )}
      ref={ref}
      {...props}
    >
      <SelectValue className="w-full truncate" placeholder={placeholder} />

      {allowDeselect && value ? (
        <div
          aria-label="Clear"
          className={inputSectionClasses({
            side: "right",
            pointerEvents: true,
            pointer: true,
          })}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onValueChange?.("");
            }
          }}
          onPointerDown={(e) => {
            e.preventDefault();
            onValueChange?.("");
          }}
          role="button"
          // Make the div focusable and keyboard accessible
          tabIndex={0}
        >
          <Icon icon="x" size="!xs" weight="bold" />
        </div>
      ) : (
        <Icon
          className={inputSectionClasses({ side: "right" })}
          icon="chevronDown"
          size="!xs"
          weight="bold"
        />
      )}
    </SelectTrigger>
  ),
);

/**
 * Radix UI (API reference): https://www.radix-ui.com/docs/primitives/components/select#api-reference
 *
 * shadcn/ui docs: https://ui.shadcn.com/docs/components/select
 *
 * Ensure that the Combobox trigger matches the
 * design of the Select trigger.
 */
const Select = React.forwardRef<
  React.ElementRef<typeof SelectTrigger>,
  SelectProps
>(
  (
    {
      allowDeselect,
      "aria-describedby": ariaDescribedBy,
      "aria-invalid": ariaInvalid,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- lint-backlog: binding kept on purpose (prop API, rest-spread exclusion or declaration-merge signature); removing it changes the API
      className,
      id,
      options,
      placeholder,
      selectContentProps,
      showCheck = true,
      sorting = "asc",
      trigger,

      ...props
    },
    ref,
  ) => {
    options = sortOptions(options, sorting);

    const flatOptions = React.useMemo(() => {
      return getFlatOptions(options);
    }, [options]);

    return (
      <SelectPrimitive.Root {...props}>
        {trigger ?? (
          <DefaultSelectTrigger
            allowDeselect={allowDeselect}
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            id={id}
            onValueChange={props.onValueChange}
            placeholder={placeholder}
            ref={ref}
            value={props.value}
          />
        )}

        <SelectContent {...selectContentProps}>
          {isGroupedOptions(options) ? (
            options.map((group, groupIndex) => (
              <React.Fragment key={group.group}>
                <SelectGroup key={group.group}>
                  <SelectLabel>{group.group}</SelectLabel>

                  {group.items.map((item) => (
                    <SelectItem
                      className="justify-between"
                      disabled={item.disabled}
                      key={item.value}
                      showCheck={showCheck}
                      value={item.value}
                    >
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectGroup>

                {groupIndex < options.length - 1 && <SelectSeparator />}
              </React.Fragment>
            ))
          ) : (
            <SelectGroup>
              {flatOptions.map((item) => (
                <SelectItem
                  className="justify-between"
                  disabled={item.disabled}
                  key={item.value}
                  showCheck={showCheck}
                  value={item.value}
                >
                  {item.label}
                </SelectItem>
              ))}
            </SelectGroup>
          )}
        </SelectContent>
      </SelectPrimitive.Root>
    );
  },
);
Select.displayName = SelectPrimitive.Root.displayName;

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    className={cn(
      "flex h-9 w-full items-center justify-between whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-muted-foreground [&>span]:line-clamp-1",
      className,
    )}
    ref={ref}
    {...props}
  >
    {children}
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    ref={ref}
    {...props}
  >
    <Icon icon="chevronUp" size="!xs" weight="bold" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className,
    )}
    ref={ref}
    {...props}
  >
    <Icon icon="chevronDown" size="!xs" weight="bold" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      className={cn(
        "relative z-50 max-h-[--radix-select-content-available-height] w-[var(--radix-select-trigger-width,100%)] origin-[--radix-select-content-transform-origin] overflow-y-auto overflow-x-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className,
      )}
      position={position}
      ref={ref}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" &&
            "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]",
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    className={cn("px-2 py-1.5 text-sm font-semibold", className)}
    ref={ref}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item> & {
    showCheck?: boolean;
  }
>(({ className, children, showCheck = true, ...props }, ref) => (
  <SelectPrimitive.Item
    className={cn(
      "relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-none focus:bg-ui-accent focus:text-ui-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
      className,
    )}
    ref={ref}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>

    {showCheck && (
      <SelectPrimitive.ItemIndicator>
        <Icon icon="check" size="!xs" weight="bold" />
      </SelectPrimitive.ItemIndicator>
    )}
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    className={cn("-mx-1 my-1 h-px bg-muted", className)}
    ref={ref}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  DefaultSelectTrigger as SelectTrigger,
  // SelectGroup,
  // SelectValue,
  // SelectTrigger,
  // SelectContent,
  // SelectLabel,
  // SelectItem,
  // SelectSeparator,
  // SelectScrollUpButton,
  // SelectScrollDownButton,
  type SelectProps,
  type DefaultSelectTriggerProps as SelectTriggerProps,
};
